import { parseApiError } from '../../api/request';
import { captureException as captureLogRocketException, initializeLogRocket } from '../logrocket';
import {
    captureException as captureSentryException,
    initializeSentry,
    withScope as withSentryScope,
} from '../sentry';

export const initializeErrorTracking = (sentryDsn: string, logRocketDsn: string) => {
    const env = config.env;

    initializeSentry(env, sentryDsn);
    initializeLogRocket(logRocketDsn);
};

export const isRequestCancelled = (error: Error): boolean =>
    error != null &&
    typeof error === 'object' &&
    ((error as { name?: string }).name === 'AbortError' ||
        (error as { name?: string }).name === 'CanceledError' ||
        (error as { code?: string }).code === 'ERR_CANCELED');

const isLocalEnv = (): boolean => config.env === 'local';

const shouldSkipCapture = (error: Error): boolean =>
    // Do not capture request cancellation - it is intentional, not a crash
    isRequestCancelled(error) ||
    // Do not capture errors in local environment
    isLocalEnv();

export const captureException = (error: Error) => {
    if (shouldSkipCapture(error)) {
        return null;
    }

    const apiError = parseApiError(error);

    // Do not capture 401 errors - these are expected auth/session-expired flows
    if (apiError?.code === 401) {
        return null;
    }

    captureSentryException(error, { tags: { api: true, code: apiError.code } });
    captureLogRocketException(error, { tags: { api: true, code: apiError.code } });
};

export const captureUnhandledError = (error: Error, info, callback: (id: string) => void) => {
    if (shouldSkipCapture(error)) {
        return null;
    }

    withSentryScope((scope) => {
        scope.setExtras(info);
        const eventId = captureSentryException(error);
        callback(eventId);
    });
    captureLogRocketException(error, { extra: info });
};
